// frontend/user/src/routes/transfers.tsx
//
// 对应 .pen yOObb 页面-传输列表;route: /transfers
// 设计来源:docs/superpowers/specs/2026-05-28-transfer-entry-yOObb-design.md
//   + docs/superpowers/plans/2026-06-07-transfers-persist-and-speed.md(刷新保留 + 实时速度)
//
// V1.1:IndexedDB 持久化(刷新保留列表);进行中显示实时速度 + ETA;
//   刷新后 uploading/pending → 已中断,可重新选择同一文件触发后端 bysha 断点续传。

import { useRef, useState } from 'react'
import { formatBytes } from '@dogdisk/shared'
import { useUploadStore, type UploadTask } from '../_lib/upload-store'
import { resetForRetryAndRun, pauseUpload, resumeUpload, pauseAll } from '../_lib/upload-run'
import { formatSpeed, formatEta, etaSeconds } from '../_lib/upload-speed'
import { MobileScreen } from '../components/mobile-screen'
import { BackButton } from '../components/back-button'

export function TransfersPage() {
  const tasks = useUploadStore((s) => s.tasks)
  const clearInactive = useUploadStore((s) => s.clearInactive)

  // 正在上传 section 合并 pending/uploading/paused(对齐 .pen yOObb uploadList 把暂停行也放此区)。
  const active = tasks.filter((t) => t.status === 'pending' || t.status === 'uploading' || t.status === 'paused')
  const uploadingCount = tasks.filter((t) => t.status === 'pending' || t.status === 'uploading').length
  const pausedCount = tasks.filter((t) => t.status === 'paused').length
  const interrupted = tasks.filter((t) => t.status === 'interrupted')
  const failed = tasks.filter((t) => t.status === 'error')
  const done = tasks.filter((t) => t.status === 'done')

  // 重选续传:记录待续传的 task,触发隐藏 input;选好文件后校验 name+size 一致再跑。
  const resumeInputRef = useRef<HTMLInputElement>(null)
  const [resumeTarget, setResumeTarget] = useState<UploadTask | null>(null)
  const [resumeWarn, setResumeWarn] = useState<string | null>(null)

  const onPickResume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    const target = resumeTarget
    setResumeTarget(null)
    if (!file || !target) return
    if (file.name !== target.name || file.size !== target.size) {
      setResumeWarn(`所选文件与「${target.name}」不一致,请选择原文件`)
      return
    }
    setResumeWarn(null)
    resetForRetryAndRun(target.id, file)
  }

  const triggerResume = (task: UploadTask) => {
    setResumeTarget(task)
    resumeInputRef.current?.click()
  }

  // 活动区恢复:File 在内存 → 直接续(resumeUpload);File 缺失 → 走重选(triggerResume),
  // 杜绝 resumeUpload 在 !task.file 时静默 no-op 造成的"点击无反应"。对齐失败区 onRetry 兜底模式。
  const onResumePaused = (task: UploadTask) => {
    if (task.file) resumeUpload(task.id)
    else triggerResume(task)
  }

  const onRetry = (task: UploadTask) => {
    // 同会话:File 还在内存 → 直接重试;刷新后 File 被 IDB 持久化剥离(toPersistable)
    // → 走重选文件流程(校验 name+size 一致再续传),避免静默 no-op(原 bug:重试无反应)。
    if (task.file) {
      resetForRetryAndRun(task.id, task.file)
    } else {
      triggerResume(task)
    }
  }

  return (
    <MobileScreen bg="white">
      <input
        ref={resumeInputRef}
        type="file"
        className="hidden"
        onChange={onPickResume}
        data-testid="transfers-resume-input"
      />
      <div data-frame-id="yOObb" data-testid="yOObb" className="flex flex-1 flex-col">
        <header className="flex items-center justify-between px-6 pt-[max(10px,env(safe-area-inset-top))] pb-3">
          <BackButton />
          <h1 className="text-[18px] font-semibold text-[#1A1A1A]">传输列表</h1>
          <button
            type="button"
            data-testid="transfers-clear-finished"
            onClick={clearInactive}
            aria-label="清空已结束任务"
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F6F7F8]"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M6 6l1 14a2 2 0 002 2h6a2 2 0 002-2l1-14" />
            </svg>
          </button>
        </header>

        <div
          data-testid="transfers-stats"
          className="mx-6 mt-2 flex items-center justify-between rounded-2xl bg-[#F6F7F8] px-4 py-3 text-[13px]"
        >
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#19caad]" />上传中 <span className="font-semibold tabular-nums">{uploadingCount}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />已暂停 <span className="font-semibold tabular-nums">{pausedCount}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />已完成 <span className="font-semibold tabular-nums">{done.length}</span>
            </span>
          </div>
          {uploadingCount > 0 && (
            <button
              type="button"
              data-testid="transfers-pause-all"
              onClick={pauseAll}
              className="flex items-center gap-1 text-[#6B7280]"
            >
              <CirclePauseIcon size={16} />全部暂停
            </button>
          )}
        </div>

        {resumeWarn && (
          <p data-testid="transfers-resume-warn" className="mx-6 mt-2 text-[12px] text-[#FF6B6B]">{resumeWarn}</p>
        )}

        <main className="flex-1 px-6 pb-32 pt-4">
          {tasks.length === 0 && (
            <p data-testid="transfers-empty" className="mt-12 text-center text-[13px] text-[#9CA3AF]">
              暂无上传任务
            </p>
          )}

          {active.length > 0 && (
            <Section title="正在上传" testid="transfers-section-active">
              {active.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  onPause={() => pauseUpload(t.id)}
                  onResume={() => onResumePaused(t)}
                />
              ))}
            </Section>
          )}

          {interrupted.length > 0 && (
            <Section title="已中断" testid="transfers-section-interrupted">
              {interrupted.map((t) => (
                <TaskRow key={t.id} task={t} onResume={() => triggerResume(t)} />
              ))}
            </Section>
          )}

          {failed.length > 0 && (
            <Section title="失败" testid="transfers-section-failed">
              {failed.map((t) => (
                <TaskRow key={t.id} task={t} onRetry={() => onRetry(t)} />
              ))}
            </Section>
          )}

          {done.length > 0 && (
            <Section title="已完成" testid="transfers-section-done">
              {done.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </Section>
          )}
        </main>
      </div>
    </MobileScreen>
  )
}

function Section({ title, testid, children }: { title: string; testid: string; children: React.ReactNode }) {
  return (
    <section className="mb-6" data-testid={testid}>
      <h2 className="mb-3 text-[15px] font-semibold text-[#1A1A1A]">{title}</h2>
      <ul className="flex flex-col gap-3">{children}</ul>
    </section>
  )
}

function TaskRow({
  task,
  onRetry,
  onResume,
  onPause,
}: {
  task: UploadTask
  onRetry?: () => void
  onResume?: () => void
  onPause?: () => void
}) {
  const isError = task.status === 'error'
  const isInterrupted = task.status === 'interrupted'
  const isPaused = task.status === 'paused'
  const isDone = task.status === 'done'
  const isUploading = task.status === 'uploading'
  const isPending = task.status === 'pending'
  const eta = isUploading ? etaSeconds(task.size - task.bytesUploaded, task.speedBps ?? 0) : null
  const etaText = formatEta(eta)

  return (
    <li
      data-testid={`transfers-row-${task.id}`}
      className={`flex flex-col gap-2 rounded-2xl bg-[#F6F7F8] p-4 ${isPaused ? 'ring-1 ring-[#FEF3C7]' : ''}`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="truncate text-[14px] font-medium text-[#1A1A1A]">{task.name}</span>
        {/* 上传中/排队中:暂停按钮;暂停态:恢复(播放)按钮;终态:文字状态 */}
        {(isUploading || isPending) && onPause ? (
          <button
            type="button"
            data-testid={`transfers-pause-${task.id}`}
            onClick={onPause}
            aria-label="暂停"
            className="shrink-0 text-[#9CA3AF]"
          >
            <CirclePauseIcon size={22} />
          </button>
        ) : isPaused && onResume ? (
          <button
            type="button"
            data-testid={`transfers-resume-${task.id}`}
            onClick={onResume}
            aria-label="恢复"
            className="shrink-0 text-[#19caad]"
          >
            <CirclePlayIcon size={22} />
          </button>
        ) : (
          <span className="shrink-0 text-[11px] text-[#9CA3AF] tabular-nums">
            {isError ? '失败' : isInterrupted ? '已中断' : isDone ? '完成' : `${Math.round(task.percent)}%`}
          </span>
        )}
      </div>

      {isError || isInterrupted ? (
        <div className="flex items-center justify-between">
          <p className="text-[12px] text-[#FF6B6B]">
            {isInterrupted ? '已中断 — 重新选择文件续传' : (task.error ?? '上传失败')}
          </p>
          {isInterrupted && onResume && (
            <button
              type="button"
              data-testid={`transfers-reselect-${task.id}`}
              onClick={onResume}
              className="h-7 rounded-full bg-[#19caad] px-3 text-[12px] font-semibold text-white"
            >
              重新选择
            </button>
          )}
          {isError && onRetry && (
            <button
              type="button"
              data-testid={`transfers-retry-${task.id}`}
              onClick={onRetry}
              className="h-7 rounded-full bg-[#19caad] px-3 text-[12px] font-semibold text-white"
            >
              重试
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#F1F2F4]">
            <div
              className={
                isPaused
                  ? 'h-full rounded-full bg-[#9CA3AF]'
                  : isDone
                    ? 'h-full rounded-full bg-[#19caad]'
                    : 'h-full rounded-full bg-[#1971ca] transition-all'
              }
              style={{ width: `${task.percent}%` }}
            />
          </div>
          {isUploading &&
            (task.phase === 'preparing' || task.phase === 'finalizing' ? (
              <p className="text-[11px] text-[#6B7280]">
                {task.phase === 'preparing'
                  ? `正在准备上传: ${Math.round(task.hashPercent ?? 0)}%`
                  : '正在完成上传'}
              </p>
            ) : (
              <div className="flex items-center justify-between text-[11px] text-[#6B7280] tabular-nums">
                <span data-testid={`transfers-speed-${task.id}`} className="flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19V5M5 12l7-7 7 7" />
                  </svg>
                  {formatSpeed(task.speedBps)}
                </span>
                {etaText && <span data-testid={`transfers-eta-${task.id}`}>{etaText}</span>}
              </div>
            ))}
          {isPaused && (
            <p className="text-[11px] font-medium text-[#F59E0B]">已暂停 · 点击恢复</p>
          )}
        </>
      )}

      <p className="text-[11px] text-[#9CA3AF] tabular-nums">
        {formatBytes(task.bytesUploaded)} / {formatBytes(task.size)}
      </p>
    </li>
  )
}

// lucide circle-pause / circle-play 内联(项目 user SPA 用内联 svg,不引 lucide-react)。
function CirclePauseIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="10" y1="15" x2="10" y2="9" />
      <line x1="14" y1="15" x2="14" y2="9" />
    </svg>
  )
}

function CirclePlayIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polygon points="10 8 16 12 10 16 10 8" />
    </svg>
  )
}
